import { useQuery, useMutation } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { filterObject } from "../../utils/helpers";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSystem, isAppPlatformSelected } from "@/utils/hooks/useLocation";

// =================
// Queries
// =================

export const useHousingPermitsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["houses", params],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-contracts/houses`, {
        params: filterObject(params),
      }),
  });
};

export const useHousingDetailsQuery = () => {
  const { id } = useParams();
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["housing-details", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/house-contracts/houses/${id}`),
    enabled: !!id,
  });
};

export const useOneHouseContractQuery = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-house-contract", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/house-contracts/${id}`),
    enabled: !!id,
  });
};

/** Fetch one house contract by id (e.g. from report/pilgrim). Use when not on housing-contracts/:id route. */
export const useOneHouseContractByIdQuery = (contractId, options = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-house-contract", contractId],
    queryFn: () => axiosProtected.get(`${currentSystem}/house-contracts/${contractId}`),
    enabled: !!contractId,
    ...options,
  });
};
export const useOneHouseCampaignsGroupsContractQuery = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-house-campaigns-groups-contract", id],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-contracts/${id}/campaigns-groups`),
    enabled: !!id,
  });
};

export const useHousingContractsQuery = (params) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["housing-contracts", params],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-contracts/list`, {
        params: filterObject(params),
      }),
  });
};

// =================
// Mutations
// =================

export const useUpdateHousingContractMutation = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (data) => {
      const formData = new FormData();
      console.log(data);
      Object.keys(data).forEach((key) => {
        const value = data[key];

        if (value !== null && value !== undefined) {
          // Special handling for SketchImageName file
          if (key === "SketchImageName" && value instanceof File) {
            formData.append(key, value);
          } else if (key === "SketchImageName" && value?.originFileObj) {
            formData.append(key, value.originFileObj);
          } else if (key === "service_center_ids") {
            formData.set(key, JSON.stringify(value));
          } else if (key !== "SketchImageName") {
            formData.append(key, value);
          }
        }
      });

      return axiosProtected.post(
        `${currentSystem}/house-contracts/houses/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },
    onSuccess: () => {
      toast.success(t("HOUSING_CONTRACT_UPDATED_SUCCESSFULLY"));
      navigate(`/app/${currentSystem}/housing-permits/${id}`);
    }
  });
};

export const useGetHouseExaminationsQuery = ({ enabled = false } = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const platformReady = isAppPlatformSelected(currentSystem);
  return useQuery({
    queryKey: ["house-examinations", currentSystem],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/reference-data`),
    enabled: !!enabled && platformReady,
    staleTime: 1000 * 60 * 5,
  });
};
